/**
 * What the project register is being asked for: the shape of the question, how
 * it is read out of the address bar and written back, and what it turns into on
 * the wire.
 *
 * It moved out of `ProjectsList.tsx` the round the toolbar grew a client, a
 * deadline window, a tag, a member, the archive and five sortable columns. Two
 * reasons, and the size of the screen file is only the second one.
 *
 * The first is that `readView` and `writeView` are RULES, not rendering. They
 * decide what a hostile `?page=` does, which of a dozen fields are allowed to
 * disappear from the URL, and whether a link somebody sent last month still
 * draws the register it drew then. Rules of that kind want a test, and a test
 * cannot mount `ProjectsList` - so they live in a React-free file with
 * `listView.test.ts` beside them (AGENTS.md §4).
 *
 * The second is that ONE description of the filter set now serves three
 * readers: the list request, the portfolio rollup above it, and the toolbar
 * that draws the controls. `listParams` and `portfolioParams` are built from
 * the same `narrowing()` so the money in the header and the rows underneath it
 * cannot end up describing different sets of projects - the same reason the API
 * keeps `buildProjectWhere` in one file for its two endpoints
 * (`crm-projects/lib/projectFilters.ts`).
 *
 * React-free, and it imports only `./lib` (which is React-free itself) and
 * `./status` (plain data). Nothing here may reach for the `ui` barrel: that
 * barrel pulls CSS in, and the node test runner cannot load it.
 */
import type { ProjectFilterParams, ProjectListParams, ProjectStatus } from "../../../lib/projects";
import { toIso } from "./lib";
import { STATUSES } from "./status";

/**
 * The two shapes the register comes in. The list is the register - everything,
 * sortable by eye, with the money on it. The roadmap is the same records grouped
 * by when they happen.
 *
 * This array is the one place that says which values are legal: the tabs in the
 * toolbar are built from it, and so is the `?view=` parsing below. A third view
 * is added here once, rather than in two lists that can disagree about what an
 * unknown value falls back to. It lives here rather than in the toolbar because
 * the toolbar is a component and this file must stay loadable without React.
 */
export const VIEWS = ["list", "roadmap"] as const;

export type View = (typeof VIEWS)[number];

/**
 * The columns the register may be ordered by - the same five the API accepts
 * (`PROJECT_SORTS` in `crm-projects/lib/projectFilters.ts`). A value that is not
 * in this list is not refused by the endpoint, it is REPLACED by the default, so
 * the honest place to reject one is here where the fallback is visible.
 */
export const SORTS = ["created", "name", "deadline", "budget", "progress"] as const;

export type Sort = (typeof SORTS)[number];

/**
 * Which side of the archive line the register is asking about. Three values and
 * not a boolean, because there are three questions: the daily list ("hide"), the
 * archive somebody opens on purpose ("only"), and a set that must not lose a row
 * to a lifecycle flag at all ("all"). The API says the same thing at the door
 * and defaults to "hide" there too.
 */
export const ARCHIVED_MODES = ["hide", "only", "all"] as const;

export type ArchivedMode = (typeof ARCHIVED_MODES)[number];

export type Dir = "asc" | "desc";

/**
 * Which way a column reads the FIRST time it is clicked.
 *
 * Not one default for all five, because they are not the same kind of number. A
 * deadline is a queue - the soonest one is the one that matters, so it opens
 * ascending. A budget is a magnitude and the big ones are the interesting ones,
 * so it opens descending. A name is a lookup and opens A to Z. "Created"
 * descending is "what is new", which is what the register opens on.
 *
 * A `Record` rather than a ternary: a sort added to `SORTS` without a direction
 * is then a type error rather than a column that quietly opens the wrong way
 * round - the kind of bug nobody files, because the list is not empty, it is
 * just showing the least useful end of itself.
 */
const NATURAL_DIR: Record<Sort, Dir> = {
  created: "desc",
  name: "asc",
  deadline: "asc",
  budget: "desc",
  progress: "desc",
};

/** The design's page size for a list, and the board's ceiling. The roadmap is
 *  never paged - a column that stops at row 25 is a column that lies - and 100
 *  is what the endpoint's `pageSize` allows. */
export const PAGE_SIZE = 25;
export const BOARD_SIZE = 100;

/** Everything that decides which projects are on screen, and in what shape. */
export interface ListView {
  /** The COMMITTED search term. The typing buffer lives in the screen, behind a
   *  debounce, and never comes here - a URL that changed once per keystroke
   *  would turn the back button into a machine that spells the word backwards. */
  q: string;
  status: ProjectStatus | "all";
  /** A client id, or "" for every client. */
  clientId: string;
  /**
   * The deadline window, as two `YYYY-MM-DD` days or "" for open-ended.
   *
   * Days and not instants, because that is what the operator picked and what a
   * shared link should still mean tomorrow. The conversion to the instants the
   * API wants happens once, in `narrowing()`, and it is not a formality - see
   * the note there about which midnight is meant.
   */
  deadlineFrom: string;
  deadlineTo: string;
  /** One tag, or "". The API takes one value on purpose: two would immediately
   *  raise "any or all of them", which is a question nobody has answered yet. */
  tag: string;
  /** One person's id, or "". Matches `Project.members`, not the task assignee. */
  member: string;
  archived: ArchivedMode;
  sort: Sort;
  dir: Dir;
  view: View;
  page: number;
}

/**
 * Every field that NARROWS the set, at its "no opinion" value.
 *
 * Named as a group because three things need exactly this list and would
 * otherwise each keep their own copy: the reset button, the test for whether
 * anything is filtered at all, and the defaults below. A filter added to the
 * interface and forgotten in one of those is a control that cannot be cleared,
 * or a "clear" button that never appears.
 */
const NO_FILTERS = {
  q: "",
  status: "all",
  clientId: "",
  deadlineFrom: "",
  deadlineTo: "",
  tag: "",
  member: "",
  archived: "hide",
} satisfies Pick<
  ListView,
  "q" | "status" | "clientId" | "deadlineFrom" | "deadlineTo" | "tag" | "member" | "archived"
>;

type FilterKey = keyof typeof NO_FILTERS;

const FILTER_KEYS = Object.keys(NO_FILTERS) as FilterKey[];

/** No question asked yet: the whole live register, newest first, as a list,
 *  from the top. `archived: "hide"` is part of "no question asked" - a register
 *  that has to be told to hide archived projects is one nobody ever tells. */
export const DEFAULTS: ListView = {
  ...NO_FILTERS,
  sort: "created",
  dir: "desc",
  view: "list",
  page: 1,
};

/**
 * The status filter's values: "no opinion" plus the five a project can be in.
 *
 * Built from `STATUSES` rather than typed out again, so a status added to the
 * lifecycle becomes selectable here the same day it becomes storable. "all" is
 * a filter value and not a status - it is never written to the address bar,
 * because it is the default and defaults stay out of the URL.
 */
const STATUS_FILTERS: readonly ListView["status"][] = ["all", ...STATUSES];

/** The legal value, or the default. `find` over the same array the control is
 *  built from, so a value parses here exactly when the toolbar can show it. */
function oneOf<T extends string>(legal: readonly T[], raw: string | null, fallback: T): T {
  return legal.find((v) => v === raw) ?? fallback;
}

const DAY_SHAPE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A day off the address bar, or "".
 *
 * Checked by CONSTRUCTION and not only by shape, and then checked BACK. Two
 * different failures hide behind a string that looks like a date:
 *
 * `2026-13-01` is rejected outright - `new Date` answers `Invalid Date` and
 * `toISOString` on one of those THROWS, which is a mistyped link turning the
 * register into a blank tab.
 *
 * `2026-02-31` is worse, because nothing throws. V8 parses the local
 * date-and-time form leniently and ROLLS IT OVER to the 3rd of March, so the
 * field would say the 31st of February while the list answered for a day in
 * another month. Measured, not assumed: the first version of this guard tested
 * only for `Invalid Date` and let that value straight through. Hence the
 * round-trip - a day is a day only if the date built from it says the same day
 * back.
 */
function day(raw: string | null): string {
  const v = (raw ?? "").trim();
  if (!DAY_SHAPE.test(v)) return "";
  const d = new Date(`${v}T00:00:00`);
  if (!Number.isFinite(d.getTime())) return "";
  // Local components, because the string was parsed as local time. Reading UTC
  // ones here would reject every day west of Greenwich instead.
  const back = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  return back === v ? v : "";
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

/**
 * The address bar, read.
 *
 * Every field falls back to its default instead of throwing. These values do not
 * come from this screen alone - they come from a bookmark, a pasted link, a
 * message sent last month, a saved view written by a version that had one more
 * status - so `?status=archived`, `?sort=owner` or `?page=` from a truncated
 * copy-paste has to draw the register rather than a blank tab.
 */
/**
 * The highest page number this screen will believe.
 *
 * `Number.parseInt` is happy with twenty digits, and a finite number greater
 * than one passed every check here until it reached the endpoint, where the
 * offset it implies is past what Postgres accepts - so the one hostile query
 * string that got through was answered with a rejected promise over an empty
 * card rather than with the fallback this file promises for every other field.
 * A million pages is 25 million projects; anything beyond it is a typo or a
 * truncated paste, and the honest answer to both is page one.
 */
const MAX_PAGE = 1_000_000;

export function readView(sp: URLSearchParams): ListView {
  const page = Number.parseInt(sp.get("page") ?? "", 10);
  return {
    q: (sp.get("q") ?? "").trim(),
    status: oneOf(STATUS_FILTERS, sp.get("status"), DEFAULTS.status),
    clientId: (sp.get("clientId") ?? "").trim(),
    deadlineFrom: day(sp.get("deadlineFrom")),
    deadlineTo: day(sp.get("deadlineTo")),
    tag: (sp.get("tag") ?? "").trim(),
    member: (sp.get("member") ?? "").trim(),
    archived: oneOf(ARCHIVED_MODES, sp.get("archived"), DEFAULTS.archived),
    sort: oneOf(SORTS, sp.get("sort"), DEFAULTS.sort),
    dir: oneOf(["asc", "desc"] as const, sp.get("dir"), DEFAULTS.dir),
    view: oneOf(VIEWS, sp.get("view"), DEFAULTS.view),
    page: Number.isFinite(page) && page >= 1 && page <= MAX_PAGE ? page : DEFAULTS.page,
  };
}

/**
 * The address bar, written.
 *
 * Built fresh rather than patched, and anything sitting at its default is left
 * out: an unfiltered register should look like `/projects`, because a URL
 * reading `?q=&status=all&clientId=&archived=hide&sort=created&page=1` is one
 * nobody can tell has a filter on it at a glance - and this screen now has ten
 * fields that would each add their own noise. Nothing else on this route carries
 * a parameter, so there is none to preserve.
 *
 * `status` is written even on the roadmap, where the request drops it. The board
 * ignores the filter for the length of the visit; it does not FORGET it, and
 * switching back to the list has to find the status the operator chose still
 * there.
 */
export function writeView(v: ListView): URLSearchParams {
  const sp = new URLSearchParams();
  if (v.q) sp.set("q", v.q);
  if (v.status !== DEFAULTS.status) sp.set("status", v.status);
  if (v.clientId) sp.set("clientId", v.clientId);
  if (v.deadlineFrom) sp.set("deadlineFrom", v.deadlineFrom);
  if (v.deadlineTo) sp.set("deadlineTo", v.deadlineTo);
  if (v.tag) sp.set("tag", v.tag);
  if (v.member) sp.set("member", v.member);
  if (v.archived !== DEFAULTS.archived) sp.set("archived", v.archived);
  if (v.sort !== DEFAULTS.sort) sp.set("sort", v.sort);
  if (v.dir !== DEFAULTS.dir) sp.set("dir", v.dir);
  if (v.view !== DEFAULTS.view) sp.set("view", v.view);
  if (v.page !== DEFAULTS.page) sp.set("page", String(v.page));
  return sp;
}

/**
 * The register's question as a SAVED VIEW stores it.
 *
 * Deliberately the same key/value pairs the address bar carries, because
 * applying a saved view is writing them back into it: `readView` then does every
 * bit of parsing, defaulting and degrading it already does for a link somebody
 * pasted, and a stored view inherits all of it. A second parser that knew how to
 * turn a stored object into a `ListView` directly is the thing this avoids - it
 * would be a copy of the rules above, and the copy is wrong from the first day
 * either side gains a field the other has not heard of.
 *
 * ── TWO FIELDS ARE LEFT OUT, AND EACH WOULD BREAK THE VIEW IF IT TRAVELLED ──
 *
 * The PAGE, because a view is a question and not a place in the answer. Saved on
 * page 4 and reopened next month against a set that has shrunk since, it draws
 * an empty card over a register that is full - and an empty card under a filter
 * reads as "nothing matches", not as "you are past the end".
 *
 * The SEARCH TEXT, because it is typing rather than a decision. "veeb" was in
 * the box on the way to something else; a view that keeps it selects a narrower
 * set than the one that was saved, silently, under a name that says nothing
 * about it.
 *
 * Everything else travels, INCLUDING the sort, the direction and the view. A
 * saved view that restores the filters but not the ordering is a different list
 * from the one that was saved, and the disagreement surfaces at the top of the
 * page (the first rows) rather than anywhere an operator would look for it. The
 * archive mode travels for a stronger reason still: "hide" / "only" / "all"
 * decides which rows EXIST, so a view saved from the archive that reopened on
 * the live register would be a different set of projects under the same name.
 */
export function viewQuery(v: ListView): Record<string, string> {
  return Object.fromEntries(writeView({ ...v, q: DEFAULTS.q, page: DEFAULTS.page }));
}

/**
 * A stored view, back into the register's question.
 *
 * Everything goes through `readView`, so a view written by a build that had one
 * more status or one more sort column degrades into a broader register rather
 * than breaking the screen it is merely listed on. That is the rule
 * `CrmSavedView.query` states in the schema, and this is the line that keeps it:
 * nothing here trusts a stored value, it only hands it to the parser that
 * already replaces what it does not recognise with a default.
 *
 * `q` and `page` are dropped on the way IN as well as being left out on the way
 * out. `viewQuery` never writes them today, but a row saved by an older build -
 * or by an importer - can still carry them, and a view that reopens on page 4
 * with somebody's half-typed search word is the exact failure the omission
 * exists to prevent. A guard that only holds on one side is a guard that rots.
 *
 * A non-scalar is skipped rather than stringified: `String({})` is
 * "[object Object]", which the parsers above would then dutifully accept as a
 * client id nobody has, leaving an empty list with a filter chip on it. An array
 * contributes its FIRST member, because every field here is single-valued and a
 * stored list can only have come from a version that allowed several - taking
 * one of them narrows the register, which is a view the operator can read and
 * fix, where dropping the key silently widens it.
 */
export function applyQuery(query: Record<string, unknown>): ListView {
  const sp = new URLSearchParams();
  for (const [key, raw] of Object.entries(query)) {
    if (key === "q" || key === "page") continue;
    const value = Array.isArray(raw) ? (raw[0] as unknown) : raw;
    if (typeof value === "string") sp.set(key, value);
    else if (typeof value === "number" && Number.isFinite(value)) sp.set(key, String(value));
    else if (typeof value === "boolean") sp.set(key, String(value));
  }
  return readView(sp);
}

/** Whether anything at all is narrowing the register. What the reset control
 *  hangs off: a "clear the filters" button on an unfiltered list is a button
 *  that does nothing, and one missing from a filtered list is an operator
 *  clearing five controls by hand. */
export function isFiltered(v: ListView): boolean {
  return FILTER_KEYS.some((k) => v[k] !== NO_FILTERS[k]);
}

/**
 * How many filters are narrowing what is ON SCREEN.
 *
 * What the filter row's toggle wears while the row is shut. Without it a closed
 * row is a register that is quietly narrowed with nothing on screen saying so -
 * the same failure the reset button exists to prevent, moved one fold deeper.
 *
 * Three things it deliberately does NOT count, and each of them is a way the
 * number would send somebody hunting for a filter they cannot find:
 *
 *   - The SEARCH TERM. It has its own field, which stays visible whether the row
 *     is open or shut, so counting it puts a number on the toggle for something
 *     the operator is already reading.
 *   - The second half of the DEADLINE WINDOW. Two stored fields, one question; a
 *     "2" beside a single date range looks like two filters.
 *   - STATUS on the roadmap. The board draws every status as its own column, so
 *     the value is stored but dormant there - `narrowing()` drops it from the
 *     request and the toolbar hides the control. A badge counting it would be
 *     the only thing on the screen claiming the board is narrowed.
 *
 * That last one is why this takes the whole `ListView` rather than a filter bag:
 * the answer depends on which VIEW is drawing.
 */
export function activeFilterCount(v: ListView): number {
  const dormant = (k: FilterKey) =>
    k === "q" || k === "deadlineFrom" || k === "deadlineTo" || (k === "status" && v.view === "roadmap");
  const plain = FILTER_KEYS.filter((k) => !dormant(k)).filter((k) => v[k] !== NO_FILTERS[k]).length;
  const deadline = v.deadlineFrom !== NO_FILTERS.deadlineFrom || v.deadlineTo !== NO_FILTERS.deadlineTo;
  return plain + (deadline ? 1 : 0);
}

/**
 * Every filter off, and back to page one.
 *
 * The SORT and the VIEW deliberately survive. They are not filters: they say how
 * the answer is drawn, not which projects are in it, and an operator who has put
 * the register in deadline order on the board did not ask for that to be undone
 * when they cleared a tag.
 */
export function clearFilters(v: ListView): ListView {
  return { ...v, ...NO_FILTERS, page: DEFAULTS.page };
}

/**
 * What clicking a column head does.
 *
 * Three states, not two, and the third is the reason this is a function rather
 * than a flip in the component: natural direction, reversed, then OFF - back to
 * the register's own order, newest first. The default sort is `created`, and
 * there is no created COLUMN in the list (the row draws name, status, deadline,
 * progress, budget and tasks), so without a way back a single click would leave
 * the operator unable to return to the order the screen opens in. The arrow
 * disappearing from every head is what says they are there.
 *
 * A new column always starts at its own natural direction rather than at
 * whatever the previous column was pointing - see `NATURAL_DIR`.
 *
 * Page one, always: a re-sorted page 7 is a different seven projects, and the
 * reader has no way to know that the rows moved under them.
 */
export function nextSort(v: ListView, column: Sort): Pick<ListView, "sort" | "dir" | "page"> {
  if (v.sort !== column) return { sort: column, dir: NATURAL_DIR[column], page: 1 };
  if (v.dir === NATURAL_DIR[column]) {
    return { sort: column, dir: v.dir === "asc" ? "desc" : "asc", page: 1 };
  }
  return { sort: DEFAULTS.sort, dir: DEFAULTS.dir, page: 1 };
}

/**
 * A day as the instant the API should compare against.
 *
 * LOCAL midnight, converted - not `${day}T00:00:00Z`. Deadlines are written by
 * `toIso` in `./lib`, which is local midnight too, so in Estonian summer time a
 * deadline of the 20th is stored as `2026-08-19T21:00:00.000Z`. A window built
 * in UTC would start three hours after that instant and drop the project from
 * its own day - a filter that silently misses the rows it was opened to find,
 * with a plausible-looking list left behind. Hence the same helper the forms
 * use, so the two can only ever be wrong together.
 *
 * The upper edge is the END of the day and inclusive: "deadline to the 20th"
 * means work due ON the 20th, and an exclusive bound would answer with
 * everything up to the 19th while the field says otherwise.
 */
function dayStart(d: string): string | undefined {
  return d ? toIso(d) : undefined;
}

function dayEnd(d: string): string | undefined {
  return d ? new Date(`${d}T23:59:59.999`).toISOString() : undefined;
}

/**
 * The filters that NARROW the set, in the API's spelling.
 *
 * One home for them because two endpoints have to agree: the register lists the
 * rows and `GET /projects/summary` adds up the money printed directly above
 * them. The moment the strip built its own copy, the header would start
 * describing a different set of projects than the list underneath - not loudly,
 * but by a few thousand euros, in a way nobody can see by looking.
 *
 * `status` is dropped on the ROADMAP and only there: the board draws every
 * status as a column of its own, so a status filter would empty three of the
 * four columns and explain nothing. The toolbar hides the control for the same
 * reason, and the rollup above the board has to make the same omission or it
 * would total four projects over a board showing forty.
 *
 * A value at its default is left OUT rather than sent: `archived=hide` is what
 * the endpoint does anyway, and a query string that says everything says
 * nothing when you read it in a network log.
 */
function narrowing(v: ListView, board: boolean): ProjectFilterParams {
  return {
    status: !board && v.status !== "all" ? v.status : undefined,
    clientId: v.clientId || undefined,
    deadlineFrom: dayStart(v.deadlineFrom),
    deadlineTo: dayEnd(v.deadlineTo),
    tag: v.tag || undefined,
    member: v.member || undefined,
    archived: v.archived !== DEFAULTS.archived ? v.archived : undefined,
  };
}

/**
 * The register request.
 *
 * The roadmap is never paged: it asks for one board-sized page of every status
 * at once, because a kanban column that stops at row 25 reads as "that is all
 * there is". The sort still travels - with more than `BOARD_SIZE` projects it
 * decides WHICH hundred the board is drawn from, and silently sending a
 * different order than the URL claims would make that choice unexplainable.
 */
export function listParams(v: ListView): ProjectListParams {
  const board = v.view === "roadmap";
  return {
    ...narrowing(v, board),
    q: v.q || undefined,
    sort: v.sort !== DEFAULTS.sort ? v.sort : undefined,
    dir: v.dir !== DEFAULTS.dir ? v.dir : undefined,
    page: board ? 1 : v.page,
    pageSize: board ? BOARD_SIZE : PAGE_SIZE,
  };
}

/**
 * The rollup request, or `null` when the rollup has no honest answer.
 *
 * The endpoint takes every narrowing filter the register does - and deliberately
 * not `q`. A free-text search would make the header answer for a different set
 * than the words suggest ("total budget" of whatever happened to match "veeb"),
 * so the API refuses to accept the parameter at all rather than pretend.
 *
 * That refusal has to become an absence on screen, which is what the `null` is
 * for: a strip still showing the register's total while the list below it shows
 * the three projects somebody searched for is not a summary of what the operator
 * is looking at, it is a different question answered in the same box - and at a
 * glance the big number wins. Labelling it "whole register" was the alternative
 * and it loses: the label is 11px and the figure is 18px and bold.
 *
 * Note what is NOT a reason to fall silent. Every other filter is one the rollup
 * can express, so it answers for exactly the set the rows below it come from.
 */
export function portfolioParams(v: ListView): ProjectFilterParams | null {
  if (v.q) return null;
  return narrowing(v, v.view === "roadmap");
}
